// ============================================================================
// EduPath Sprint 5 - Sync Manager
// ============================================================================
// Background sync engine that processes the offline queue.
// Handles: connectivity detection, retry with backoff, event broadcasting,
// and integration with the Sprint 5 backend validation endpoints.
// ============================================================================

import { offlineQueue } from './offlineQueueService';
import type {
  OfflineQueueItem,
  SyncManagerState,
  SyncStatus,
  SyncEvent,
  SubmitResponse,
} from '@/types/offline';

// ─── Configuration ───────────────────────────────────────────────────────────

const SYNC_CONFIG = {
  /** API base URL - EduPath production endpoint */
  apiBaseUrl: import.meta.env.VITE_API_URL ||
    'https://65563g1ia9.execute-api.eu-west-1.amazonaws.com/dev',

  /** How often to check queue for pending items (ms) */
  pollIntervalMs: 15_000,

  /** Maximum concurrent sync operations */
  maxConcurrent: 2,

  /** Timeout for individual submit requests (ms) */
  requestTimeoutMs: 30_000,

  /** Auto-clear synced items after this many ms */
  clearSyncedAfterMs: 300_000, // 5 minutes
};

// ─── Event System ────────────────────────────────────────────────────────────

type SyncEventHandler = (event: SyncEvent) => void;
const eventHandlers = new Set<SyncEventHandler>();

function emit(event: Omit<SyncEvent, 'timestamp'>): void {
  const fullEvent: SyncEvent = {
    ...event,
    timestamp: new Date().toISOString(),
  };
  eventHandlers.forEach((handler) => {
    try {
      handler(fullEvent);
    } catch (err) {
      console.error('[SyncManager] Event handler error:', err);
    }
  });
}

// ─── Internal State ──────────────────────────────────────────────────────────

let managerState: SyncManagerState = 'IDLE';
let pollTimer: ReturnType<typeof setInterval> | null = null;
let isProcessing = false;
let activeSyncs = 0;
let lastSyncAt: string | null = null;

// ─── Connectivity Detection ──────────────────────────────────────────────────

function getOnlineStatus(): boolean {
  if (typeof navigator === 'undefined') return true;
  return navigator.onLine;
}

function setupConnectivityListeners(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('online', () => {
    console.log('[SyncManager] Device came online');
    emit({ type: 'ONLINE' });
    // Immediately try to sync when coming back online
    processQueue();
  });

  window.addEventListener('offline', () => {
    console.log('[SyncManager] Device went offline');
    managerState = 'WAITING';
    emit({ type: 'OFFLINE' });
  });
}

// ─── API Communication ───────────────────────────────────────────────────────

/**
 * Get the current auth token from Cognito/Amplify.
 * Adjust this to match your auth implementation.
 */
async function getAuthToken(): Promise<string | null> {
  try {
    const keys = Object.keys(localStorage);
    // Look for Cognito ID token in localStorage
    const tokenKey = keys.find(
      (k) => k.includes('idToken') || k.includes('accessToken') || k.includes('CognitoIdentityServiceProvider')
    );
    return tokenKey ? localStorage.getItem(tokenKey) : null;
  } catch {
    return null;
  }
}

/**
 * Submit an offline assessment to the backend.
 * Calls the updated edupath-submit-assessment-dev Lambda
 * which includes offline validation, dedup, and idempotency.
 */
async function submitToBackend(
  item: OfflineQueueItem
): Promise<SubmitResponse> {
  const token = await getAuthToken();

  const controller = new AbortController();
  const timeout = setTimeout(
    () => controller.abort(),
    SYNC_CONFIG.requestTimeoutMs
  );

  try {
    const payload = {
      sessionId: item.sessionId,
      learnerId: item.learnerId,
      assessmentType: item.assessmentType,
      idempotencyKey: item.idempotencyKey,
      responses: item.responses,
      submittedAt: item.sessionMeta.completedAt,
      metadata: {
        offlineSubmission: true,
        queuedAt: item.queuedAt,
        deviceInfo: item.sessionMeta.deviceInfo,
        appVersion: item.sessionMeta.appVersion,
        startedAt: item.sessionMeta.startedAt,
        completedAt: item.sessionMeta.completedAt,
        totalDurationMs: item.sessionMeta.totalDurationMs,
        questionCount: item.sessionMeta.questionCount,
        answeredCount: item.sessionMeta.answeredCount,
      },
    };

    const response = await fetch(
      `${SYNC_CONFIG.apiBaseUrl}/assessments/sessions/${item.sessionId}/submit`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      }
    );

    const data = await response.json();

    if (response.ok) {
      return {
        success: true,
        sessionId: item.sessionId,
        status: data.status || 'COMPLETED',
        integrityFlags: data.integrityFlags || [],
        message: data.message,
      };
    }

    // Handle specific HTTP status codes from backend
    if (response.status === 409) {
      // Duplicate - already submitted (idempotency)
      return {
        success: true, // Treat as success - already synced
        sessionId: item.sessionId,
        status: 'ALREADY_SUBMITTED',
        rejectCode: 'DUPLICATE_SUBMISSION',
        message: data.message || 'Assessment already submitted',
      };
    }

    if (response.status === 410) {
      // Session expired / stale
      return {
        success: false,
        rejectCode: 'SESSION_FRESHNESS',
        message: data.message || 'Session has expired',
        errors: data.errors,
      };
    }

    if (response.status === 422) {
      // Validation failure (completeness, integrity)
      return {
        success: false,
        rejectCode: data.errors?.[0]?.code || 'VALIDATION_ERROR',
        integrityFlags: data.integrityFlags || [],
        message: data.message || 'Validation failed',
        errors: data.errors,
      };
    }

    // Other server errors - retryable
    return {
      success: false,
      message: data.message || `Server error: ${response.status}`,
      errors: data.errors,
    };
  } catch (err: unknown) {
    if (err instanceof Error && err.name === 'AbortError') {
      return { success: false, message: 'Request timed out' };
    }
    return {
      success: false,
      message: err instanceof Error ? err.message : 'Network error',
    };
  } finally {
    clearTimeout(timeout);
  }
}

// ─── Queue Processing ────────────────────────────────────────────────────────

/**
 * Process a single queue item: submit to backend and update status.
 */
async function processSingleItem(item: OfflineQueueItem): Promise<void> {
  activeSyncs++;

  try {
    // Mark as syncing in queue
    await offlineQueue.markSyncing(item.id);
    emit({ type: 'SYNC_START', item });

    const result = await submitToBackend(item);

    if (result.success) {
      // Successfully synced (or was already submitted)
      await offlineQueue.markSynced(item.id, result as Record<string, unknown>);
      emit({ type: 'ITEM_SYNCED', item });
      console.log(
        `[SyncManager] ✓ Synced session ${item.sessionId}`,
        result.integrityFlags?.length
          ? `(flags: ${result.integrityFlags.join(', ')})`
          : ''
      );
    } else if (result.rejectCode) {
      // Server rejected - no retry
      const isRetryable =
        result.rejectCode === 'VALIDATION_ERROR' && item.retryCount < 2;

      if (isRetryable) {
        await offlineQueue.markFailed(item.id, result.message || 'Validation error');
        emit({ type: 'ITEM_FAILED', item, error: result.message });
      } else {
        await offlineQueue.markRejected(
          item.id,
          result.rejectCode,
          result.message || 'Server rejected submission',
          result.integrityFlags
        );
        emit({ type: 'ITEM_REJECTED', item, error: result.message });
        console.warn(
          `[SyncManager] ✗ Rejected session ${item.sessionId}: ${result.rejectCode}`
        );
      }
    } else {
      // Network/server error - retry with backoff
      await offlineQueue.markFailed(item.id, result.message || 'Sync failed');
      emit({ type: 'ITEM_FAILED', item, error: result.message });
      console.warn(
        `[SyncManager] ⟳ Failed session ${item.sessionId}, will retry (${item.retryCount + 1}/${item.maxRetries})`
      );
    }
  } catch (err) {
    await offlineQueue.markFailed(
      item.id,
      err instanceof Error ? err.message : 'Unknown error'
    );
    emit({
      type: 'ITEM_FAILED',
      item,
      error: err instanceof Error ? err.message : 'Unknown error',
    });
  } finally {
    activeSyncs--;
  }
}

/**
 * Process all ready-to-sync items in the queue.
 */
async function processQueue(): Promise<void> {
  if (isProcessing) return;
  if (!getOnlineStatus()) {
    managerState = 'WAITING';
    return;
  }

  isProcessing = true;
  managerState = 'SYNCING';

  try {
    const readyItems = await offlineQueue.getReadyToSync();

    if (readyItems.length === 0) {
      managerState = 'IDLE';
      emit({ type: 'QUEUE_EMPTY' });

      // Auto-clear old synced items
      const cleared = await offlineQueue.clearSynced();
      if (cleared > 0) {
        console.log(`[SyncManager] Cleared ${cleared} synced items`);
      }
      return;
    }

    console.log(`[SyncManager] Processing ${readyItems.length} pending items`);

    // Process items with concurrency limit
    const batches: OfflineQueueItem[][] = [];
    for (let i = 0; i < readyItems.length; i += SYNC_CONFIG.maxConcurrent) {
      batches.push(readyItems.slice(i, i + SYNC_CONFIG.maxConcurrent));
    }

    for (const batch of batches) {
      if (!getOnlineStatus()) {
        managerState = 'WAITING';
        break;
      }
      await Promise.all(batch.map(processSingleItem));
    }

    lastSyncAt = new Date().toISOString();

    // Check if anything remains
    const remaining = await offlineQueue.getReadyToSync();
    managerState = remaining.length > 0 ? 'SYNCING' : 'IDLE';

    emit({ type: 'SYNC_COMPLETE' });
  } catch (err) {
    console.error('[SyncManager] Queue processing error:', err);
    managerState = 'ERROR';
    emit({
      type: 'SYNC_FAILED',
      error: err instanceof Error ? err.message : 'Unknown error',
    });
  } finally {
    isProcessing = false;
  }
}

// ─── Public API ──────────────────────────────────────────────────────────────

export const syncManager = {
  /**
   * Start the sync manager. Sets up polling and connectivity listeners.
   * Call once at app initialization.
   */
  start(): void {
    console.log('[SyncManager] Starting...');
    setupConnectivityListeners();

    // Initial sync attempt
    processQueue();

    // Set up periodic polling
    if (pollTimer) clearInterval(pollTimer);
    pollTimer = setInterval(() => {
      processQueue();
    }, SYNC_CONFIG.pollIntervalMs);

    console.log(
      `[SyncManager] Polling every ${SYNC_CONFIG.pollIntervalMs / 1000}s`
    );
  },

  /**
   * Stop the sync manager. Clears polling timer.
   */
  stop(): void {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
    managerState = 'IDLE';
    console.log('[SyncManager] Stopped');
  },

  /**
   * Pause sync operations (user-initiated).
   */
  pause(): void {
    managerState = 'PAUSED';
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
    console.log('[SyncManager] Paused');
  },

  /**
   * Resume sync operations after pause.
   */
  resume(): void {
    managerState = 'IDLE';
    this.start();
    console.log('[SyncManager] Resumed');
  },

  /**
   * Trigger an immediate sync attempt.
   */
  async syncNow(): Promise<void> {
    console.log('[SyncManager] Manual sync triggered');
    await processQueue();
  },

  /**
   * Retry a specific failed/rejected item.
   */
  async retryItem(itemId: string): Promise<boolean> {
    const item = await offlineQueue.getById(itemId);
    if (!item) return false;

    // Reset for retry
    await offlineQueue.updateItem(itemId, {
      status: 'PENDING',
      retryCount: 0,
      errorMessage: null,
      rejectCode: null,
      nextRetryAt: null,
    });

    // Process immediately
    await processQueue();
    return true;
  },

  /**
   * Discard a queue item (user chose to abandon).
   */
  async discardItem(itemId: string): Promise<boolean> {
    const item = await offlineQueue.getById(itemId);
    if (!item) return false;
    await offlineQueue.remove(itemId);
    return true;
  },

  /**
   * Get current sync status for UI display.
   */
  async getStatus(): Promise<SyncStatus> {
    const stats = await offlineQueue.getStats();

    // Find earliest next retry
    let nextRetryAt: string | null = null;
    if (stats.failed > 0) {
      const failed = await offlineQueue.getByStatus('FAILED');
      const retries = failed
        .map((i) => i.nextRetryAt)
        .filter(Boolean)
        .sort();
      nextRetryAt = retries[0] || null;
    }

    return {
      state: managerState,
      pendingCount: stats.pending,
      syncingCount: stats.syncing,
      failedCount: stats.failed,
      rejectedCount: stats.rejected,
      conflictCount: stats.conflict,
      lastSyncAt,
      isOnline: getOnlineStatus(),
      nextRetryAt,
    };
  },

  /**
   * Get all queue items for display in the UI.
   */
  async getQueueItems(): Promise<OfflineQueueItem[]> {
    return offlineQueue.getAll();
  },

  /**
   * Subscribe to sync events.
   * Returns an unsubscribe function.
   */
  onEvent(handler: SyncEventHandler): () => void {
    eventHandlers.add(handler);
    return () => {
      eventHandlers.delete(handler);
    };
  },

  /**
   * Get current manager state.
   */
  getState(): SyncManagerState {
    return managerState;
  },
};

export default syncManager;
